//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateUtils } from 'unison';

import { getUserTweets } from '@apis/twitter-analytics/twitterAnalyticsApi';
import { IMaterializedTweetWithMetrics } from '@apis/twitter-analytics/twitterAnalyticsApiModel';
import { isTwitterMediaPostConfig } from '@apis/universal-posts/config/universalScheduledPostsConfigUtils';
import { searchScheduledPosts } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApi';
import {
  ISearchScheduledPostsRequest,
  IScheduledTweet,
} from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import {
  UniversalPostType,
  UniversalPostingGroupName,
} from '@apis/universal-posts/universalPostsApiModel';
import { getAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import PostableStatus from '@models/PostableStatus';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { ITwitterSummaryPageState } from '@twitterV2/redux/summary/twitterSummaryModel';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

const initialState: ITwitterSummaryPageState = {
  upcomingTweets: [],
  recentTweets: [],
  flags: {
    isFetchingUpcomingTweets: false,
    isFetchingRecentTweets: false,
  },
  errors: {
    fetchUpcomingTweetsError: undefined,
    fetchRecentTweetsError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.TwitterSummary,
  initialState,
  reducers: {
    requestUpcomingTweets(state) {
      state.flags.isFetchingUpcomingTweets = true;
      state.errors.fetchUpcomingTweetsError = undefined;
    },
    receiveUpcomingTweetsSucceeded(
      state,
      action: PayloadAction<IScheduledTweet[]>,
    ) {
      state.flags.isFetchingUpcomingTweets = false;
      state.upcomingTweets = [...action.payload];
    },
    receiveUpcomingTweetsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingUpcomingTweets = false;
      state.errors.fetchUpcomingTweetsError = action.payload;
    },
    requestRecentTweets(state) {
      state.flags.isFetchingRecentTweets = true;
      state.errors.fetchRecentTweetsError = undefined;
    },
    receiveRecentTweetsSucceeded(
      state,
      action: PayloadAction<IMaterializedTweetWithMetrics[]>,
    ) {
      state.recentTweets = action.payload;
      state.flags.isFetchingRecentTweets = false;
    },
    receiveRecentTweetsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingRecentTweets = false;
      state.errors.fetchRecentTweetsError = action.payload;
    },
  },
});

export const getUpcomingScheduledTweets = createThunk(
  () => async (dispatch, getState) => {
    const { isFetchingUpcomingTweets } = getState().twitter.summary.flags;

    if (isFetchingUpcomingTweets) {
      return;
    }

    dispatch(actions.requestUpcomingTweets());

    try {
      const searchRequest: ISearchScheduledPostsRequest = {
        after: DateUtils.today(),
        limit: 4,
        status: [
          PostableStatus.Dispatched,
          PostableStatus.Pending,
          PostableStatus.Processing,
          PostableStatus.Rescheduled,
          PostableStatus.Scheduled,
        ],
        groups: [UniversalPostingGroupName.TwitterPostingGroup],
      };
      const { data: scheduledPosts } = await searchScheduledPosts(
        searchRequest,
      );

      const tweets = scheduledPosts.map((scheduledPost) => {
        if (scheduledPost.postType === UniversalPostType.TwitterMediaPost) {
          return scheduledPost as IScheduledTweet;
        } else {
          throw new Error(
            `Retrieved invalid post config type: ${scheduledPost.config['@type']}`,
          );
        }
      });

      dispatch(actions.receiveUpcomingTweetsSucceeded(tweets));
    } catch (error) {
      dispatch(
        actions.receiveUpcomingTweetsFailed(getRequestErrorMessage(error)),
      );
    }
  },
);

export const getRecentTweets = createThunk(() => async (dispatch, getState) => {
  const twitterUserId = getState().twitter.user.user?.id;
  const twitterUsername = getState().twitter.user.user?.screenName;

  const userTimeZoneId = getState().session.timeZone.id;
  const dateRange = getAliasedDateRange('last30Days', userTimeZoneId);

  if (!isDefined(twitterUserId) || !isDefined(twitterUsername)) {
    return;
  }

  dispatch(actions.requestRecentTweets());

  try {
    const tweetData = await getUserTweets({
      twitterUsername,
      twitterUserId,
      startTime: dateRange.startDate,
      endTime: dateRange.endDate,
      limit: 8,
      offset: 0,
    });

    dispatch(actions.receiveRecentTweetsSucceeded(tweetData.data.tweets));
  } catch (e) {
    dispatch(actions.receiveRecentTweetsFailed(getRequestErrorMessage(e)));
  }
});

export default reducer;
